import type { CaseDetail } from '@/lib/api/quality-types.js';

import { chapterLabel } from './chapters.js';

/** One case step surfaced in the time-sink strip (S12, AC 28): the biggest single contributors to
 *  the run's wall clock, each seekable to its moment in the recording. */
export interface TimeSink {
  caseId: string;
  caseTitle: string;
  stepName: string;
  durationMs: number;
  timecodeMs: number | null;
  component?: string;
  /** The file `timecodeMs` is measured in, when the case recorded several. */
  videoKey?: string;
}

/** A step's `duration_ms` is string-or-number on the wire and may be absent; a non-positive or
 *  non-finite value carries no signal (it isn't a "sink"), so it's treated the same as absent. */
function parseStepDurationMs(
  value: number | string | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * The biggest single contributors to the run's wall clock, across every case's steps (AC 28/29):
 * the top `limit` (default 3) steps by `duration_ms`, plus their share of the total and the total
 * itself — the numbers behind "3 steps = 41 of 48 min". A step with no usable duration is skipped
 * entirely rather than counted as zero, and `cases` being absent/empty yields an all-zero result
 * with no entries.
 */
export function topTimeSinks(
  cases: CaseDetail[] | undefined,
  options?: { limit?: number },
): { entries: TimeSink[]; entriesMs: number; totalMs: number } {
  const limit = options?.limit ?? 3;
  const sinks: TimeSink[] = [];
  let totalMs = 0;

  for (const caseDetail of cases ?? []) {
    for (const step of Array.isArray(caseDetail.steps)
      ? caseDetail.steps
      : []) {
      const durationMs = parseStepDurationMs(step.duration_ms);
      if (durationMs === null) continue;
      totalMs += durationMs;
      sinks.push({
        caseId: caseDetail.id,
        caseTitle: caseDetail.title || caseDetail.id,
        stepName: step.name || 'step',
        durationMs,
        timecodeMs: step.timecode_ms ?? null,
        component: step.component,
        videoKey: step.video_key,
      });
    }
  }

  const entries = sinks
    .sort((a, b) => b.durationMs - a.durationMs)
    .slice(0, limit);
  const entriesMs = entries.reduce((sum, entry) => sum + entry.durationMs, 0);
  return { entries, entriesMs, totalMs };
}

/**
 * The one step name every entry shares, or null when they differ. On a real run the top three sinks
 * are routinely the SAME step across three cases (a synchronization wait), and saying that once —
 * "all 3 are …" — is the whole finding; three separate sentences hide it. Compared case-insensitively
 * on trimmed names, since the same step reaches different cases with incidental whitespace.
 */
export function sharedStepName(entries: TimeSink[]): string | null {
  const [firstEntry] = entries;
  if (!firstEntry || entries.length < 2) return null;
  const normalized = (name: string): string => chapterLabel(name).toLowerCase();
  const firstName = chapterLabel(firstEntry.stepName);
  return entries.every(
    (entry) => normalized(entry.stepName) === firstName.toLowerCase(),
  )
    ? firstName
    : null;
}
